/**
 * Account layout (/account)
 *
 * Shared shell for the signed-in user's workspace: identity header, sign-out,
 * a first-run welcome (after self-service signup), and tab navigation between
 * the two sub-pages — "My estimates" (list + claiming) and "My information"
 * (profile). The active sub-page renders through <Outlet/>.
 */

import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut, Sparkles, Plus, FilePen, UserPen, ShieldCheck, Store, Inbox } from 'lucide-react';
import { useAuth, type RetailerMembership } from '../context/AuthContext';

/**
 * One line per CONFIRMED retailer membership. Only approved rows are shown:
 * the matcher runs for every new account, and a pending / rejected / unmatched
 * proposal must never surface retailer wording to an ordinary customer. People
 * who came through the join page were told there that Trusscore confirms the
 * store first.
 */
const MembershipLine: React.FC<{ m: RetailerMembership }> = ({ m }) => (
  <Trans i18nKey="retailer.approved" ns="account"
    values={{ retailer: m.retailerName ?? '' }} components={{ b: <strong /> }} />
);

export const Account: React.FC = () => {
  const { t } = useTranslation('account');
  const { user, logout, refreshSession } = useAuth();
  // Approvals happen elsewhere (Trusscore or a retailer manager); re-read the
  // session on arrival so the retailer card and menu reflect them.
  useEffect(() => { refreshSession(); }, [refreshSession]);
  const memberships = (user?.retailerMemberships ?? []).filter(m => m.status === 'approved' && m.retailerName);
  const navigate = useNavigate();
  // First-run welcome: signup() lands new, verified registrants on /account
  // with ?welcome=1 so they get a "create your first estimate" CTA. Existing
  // users (normal login) never see it.
  const [searchParams] = useSearchParams();
  const isFirstRun = searchParams.get('welcome') === '1';

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link d-inline-flex align-items-center gap-2 ${isActive ? 'active' : ''}`;

  return (
    <div className="container px-0 px-sm-3 py-4" style={{ maxWidth: 860 }}>
      <div className="d-flex align-items-center justify-content-between mb-4 gap-2">
        <div className="text-truncate">
          <h3 className="fw-bold mb-1">{t('title')}</h3>
          <p className="text-muted small mb-0 text-truncate">{user?.name || user?.email}</p>
        </div>
        <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 flex-shrink-0" onClick={() => logout()}>
          <LogOut size={16} /> {t('auth:logout.title')}
        </button>
      </div>

      {isFirstRun && (
        <div className="card border-primary shadow-sm mb-4">
          <div className="card-body p-4 d-flex align-items-start gap-3">
            <div
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(13,110,253,0.1)' }}
            >
              <Sparkles size={22} className="text-primary" />
            </div>
            <div className="flex-grow-1">
              {/* Two whole greetings rather than a base string plus an
                  appended name — the name's position in the sentence is the
                  translator's call, not ours. */}
              <h5 className="fw-bold mb-1">
                {user?.name
                  ? t('welcome.titleNamed', { name: user.name.split(' ')[0] })
                  : t('welcome.title')}
              </h5>
              <p className="text-muted small mb-3">{t('welcome.body')}</p>
              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                onClick={() => navigate('/')}
              >
                <Plus size={16} /> {t('welcome.cta')}
              </button>
            </div>
          </div>
        </div>
      )}

      {memberships.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-body p-3 d-flex align-items-start gap-3">
            <Store size={22} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <h6 className="fw-bold mb-1">{t('retailer.heading')}</h6>
              {memberships.map((m, i) => (
                <p key={`${m.retailerId ?? 'none'}-${i}`} className="small mb-1">
                  <MembershipLine m={m} />
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      <ul className="nav nav-pills gap-2 mb-4">
        <li className="nav-item">
          <NavLink end to="/account" className={tabClass}><FilePen size={16} /> {t('tabs.estimates')}</NavLink>
        </li>
        {/* Only for accounts an admin attached to a partner location (a retailer store / installer). */}
        {!!user?.partnerLocations?.length && (
          <li className="nav-item">
            <NavLink to="/account/inbox" className={tabClass}><Inbox size={16} /> {t('tabs.inbox')}</NavLink>
          </li>
        )}
        <li className="nav-item">
          <NavLink to="/account/profile" className={tabClass}><UserPen size={16} /> {t('tabs.information')}</NavLink>
        </li>
        <li className="nav-item">
          <NavLink to="/account/security" className={tabClass}><ShieldCheck size={16} /> {t('tabs.security')}</NavLink>
        </li>
      </ul>

      <Outlet />
    </div>
  );
};

export default Account;
